import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useBaseContext, createBaseState } from './shared/useBaseContext';
import { useAuth } from '../hooks/useAuth';

const DMContext = createContext();

// DM-specific actions
const dmActions = {
  SET_CONVERSATIONS: 'SET_CONVERSATIONS',
  ADD_CONVERSATION: 'ADD_CONVERSATION',
  UPDATE_CONVERSATION: 'UPDATE_CONVERSATION',
  SET_ACTIVE_CONVERSATION: 'SET_ACTIVE_CONVERSATION',
  SET_DM_MESSAGES: 'SET_DM_MESSAGES',
  ADD_DM_MESSAGE: 'ADD_DM_MESSAGE',
  SET_TYPING_USERS: 'SET_TYPING_USERS',
  UPDATE_READ_STATUS: 'UPDATE_READ_STATUS',
  SET_USER_SUGGESTIONS: 'SET_USER_SUGGESTIONS',
  SET_SEARCH_RESULTS: 'SET_SEARCH_RESULTS',
  BATCH_UPDATE_UNREAD_COUNTS: 'BATCH_UPDATE_UNREAD_COUNTS'
};

// DM-specific reducer
const dmReducer = (state, action) => {
  switch (action.type) {
    case dmActions.SET_CONVERSATIONS:
      return {
        ...state,
        conversations: action.payload,
        loading: false,
        error: null
      };
    case dmActions.ADD_CONVERSATION:
      return {
        ...state,
        conversations: [action.payload, ...state.conversations]
      };
    case dmActions.UPDATE_CONVERSATION:
      return {
        ...state,
        conversations: state.conversations.map(conv =>
          conv.conversation_id === action.payload.conversation_id
            ? { ...conv, ...action.payload }
            : conv
        )
      };
    case dmActions.SET_ACTIVE_CONVERSATION:
      return {
        ...state,
        activeConversationId: action.payload
      };
    case dmActions.SET_DM_MESSAGES:
      return {
        ...state,
        messagesByConversation: {
          ...state.messagesByConversation,
          [action.payload.conversationId]: action.payload.messages
        }
      };
    case dmActions.ADD_DM_MESSAGE:
      const { conversationId, message } = action.payload;
      return {
        ...state,
        messagesByConversation: {
          ...state.messagesByConversation,
          [conversationId]: [
            ...(state.messagesByConversation[conversationId] || []),
            message
          ]
        }
      };
    case dmActions.SET_TYPING_USERS:
      return {
        ...state,
        typingUsers: {
          ...state.typingUsers,
          [action.payload.conversationId]: action.payload.userIds
        }
      };
    case dmActions.UPDATE_READ_STATUS:
      return {
        ...state,
        conversations: state.conversations.map(conv =>
          conv.conversation_id === action.payload.conversationId
            ? { ...conv, unread_count: 0 }
            : conv
        )
      };
    case dmActions.SET_USER_SUGGESTIONS:
      return {
        ...state,
        userSuggestions: action.payload
      };
    case dmActions.SET_SEARCH_RESULTS:
      return {
        ...state,
        searchResults: action.payload
      };
    case dmActions.BATCH_UPDATE_UNREAD_COUNTS:
      return {
        ...state,
        conversations: state.conversations.map(conv => {
          const update = action.payload.find(u => u.conversationId === conv.conversation_id);
          return update ? { ...conv, unread_count: update.unreadCount } : conv;
        })
      };
    default:
      return state;
  }
};

const initialState = createBaseState({
  conversations: [],
  activeConversationId: null,
  messagesByConversation: {},
  typingUsers: {},
  userSuggestions: {
    teamMembers: [],
    tournamentParticipants: [],
    recentUsers: []
  },
  searchResults: [],
  subscriptions: {}
});

export const DMProvider = ({ children }) => {
  const { state, dispatch, setError, clearError } = useBaseContext(initialState, dmReducer);
  const { user, isAuthenticated } = useAuth();

  // Refs for cleanup and performance
  const subscriptionsRef = useRef({});
  const typingTimeoutsRef = useRef({});
  const pollingIntervalRef = useRef(null);
  const lastUnreadCountsRef = useRef(new Map());

  // Load DM conversations
  const loadConversations = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const { data, error } = await supabase
        .from('recent_dm_conversations')
        .select('*')
        .order('last_message_at', { ascending: false });

      if (error) throw error;
      
      // Optimized: Fetch all last messages in a single query instead of N+1 queries
      const messageIds = (data || [])
        .filter(conv => conv.last_message_id)
        .map(conv => conv.last_message_id);
      
      let lastMessages = {};
      if (messageIds.length > 0) {
        const { data: messagesData, error: messagesError } = await supabase
          .from('chat_messages')
          .select('id, message')
          .in('id', messageIds)
          .eq('is_dm', true);
          
        if (!messagesError && messagesData) {
          lastMessages = Object.fromEntries(
            messagesData.map(msg => [msg.id, msg.message])
          );
        }
      }
      
      // Enhance conversations with fetched messages
      const conversationsWithMessages = (data || []).map(conversation => {
        if (conversation.last_message_id && lastMessages[conversation.last_message_id]) {
          return {
            ...conversation,
            last_message_text: lastMessages[conversation.last_message_id]
          };
        }
        return conversation;
      });
      
      dispatch({ type: dmActions.SET_CONVERSATIONS, payload: conversationsWithMessages });
      return conversationsWithMessages;
    } catch (error) {
      console.error('Failed to load DM conversations:', error);
      setError('Failed to load conversations');
    }
  }, [isAuthenticated, dispatch, setError]);

  // Create or get DM conversation
  const createOrGetConversation = useCallback(async (otherUserId) => {
    if (!isAuthenticated || !user?.id) return null;

    try {
      const { data, error } = await supabase
        .rpc('get_or_create_dm_conversation', { other_user_id: otherUserId });

      if (error) throw error;
      
      // Reload conversations to get the updated list
      await loadConversations();
      
      // Set as active conversation using dispatch directly
      dispatch({ type: dmActions.SET_ACTIVE_CONVERSATION, payload: data });
      
      return data;
    } catch (error) {
      console.error('Failed to create conversation:', error);
      setError('Failed to create conversation');
      return null;
    }
  }, [isAuthenticated, user?.id, setError, loadConversations, dispatch]);

  // Load messages for a specific conversation
  const loadConversationMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;

    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select(`
          id,
          message,
          created_at,
          message_type,
          user_id,
          users!chat_messages_profile_user_fkey (
            username,
            avatar_url,
            country_code
          )
        `)
        .eq('conversation_id', conversationId)
        .eq('is_dm', true)
        .order('created_at', { ascending: false })
        .limit(50);

      // If join fails, use fallback approach
      if (error) {
        const { data: messagesData, error: messagesError } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('conversation_id', conversationId)
          .eq('is_dm', true)
          .order('created_at', { ascending: false })
          .limit(50);

        if (messagesError) {
          console.error('Fallback messages query also failed:', messagesError);
          throw messagesError;
        }

        if (messagesData && messagesData.length > 0) {
          const userIds = [...new Set(messagesData.map(m => m.user_id))];
          const { data: usersData, error: usersError } = await supabase
            .from('users')
            .select('id, username, avatar_url, country_code')
            .in('id', userIds);

          if (usersError) throw usersError;

          const combinedData = messagesData.map(message => ({
            ...message,
            users: usersData.find(u => u.id === message.user_id)
          }));

          // Reverse to show chronological order (oldest first)
          dispatch({ 
            type: dmActions.SET_DM_MESSAGES, 
            payload: { conversationId, messages: combinedData.reverse() }
          });
        } else {
          // No messages yet, set empty array
          dispatch({ 
            type: dmActions.SET_DM_MESSAGES, 
            payload: { conversationId, messages: [] }
          });
        }
      } else if (error) {
        throw error;
      } else {
        // Reverse to show chronological order (oldest first)
        const messagesInOrder = data ? [...data].reverse() : [];
        dispatch({ 
          type: dmActions.SET_DM_MESSAGES, 
          payload: { conversationId, messages: messagesInOrder }
        });
      }
    } catch (error) {
      console.error('Failed to load conversation messages:', error);
      setError('Failed to load messages');
    }
  }, [dispatch, setError]);

  // Send DM message
  const sendDMMessage = useCallback(async (conversationId, message) => {
    if (!isAuthenticated || !user?.id || !message.trim()) return;

    try {
      const messageData = {
        message: message.trim(),
        user_id: user.id,
        message_type: 'text',
        conversation_id: conversationId,
        is_dm: true
      };

      // Try with join first
      let { data, error } = await supabase
        .from('chat_messages')
        .insert([messageData])
        .select(`
          id,
          message,
          created_at,
          message_type,
          user_id,
          conversation_id,
          is_dm,
          users!chat_messages_profile_user_fkey (
            username,
            avatar_url,
            country_code
          )
        `)
        .single();

      // If join fails (PGRST200 or 400 Bad Request), try without join
      if (error && (error.code === 'PGRST200' || error.message?.includes('400') || error.status === 400)) {
        console.log('Sending DM with fallback (no join)...');
        const { data: insertData, error: insertError } = await supabase
          .from('chat_messages')
          .insert([messageData])
          .select('*')
          .single();

        if (insertError) throw insertError;

        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('id, username, avatar_url, country_code')
          .eq('id', user.id)
          .single();

        if (userError) throw userError;

        data = {
          ...insertData,
          users: userData
        };
      } else if (error) {
        throw error;
      }
      
      // Message will be added via real-time subscription
      return data;
    } catch (error) {
      console.error('Failed to send DM:', error);
      setError('Failed to send message');
      throw error;
    }
  }, [isAuthenticated, user?.id, setError]);

  // Track which conversations we've recently marked as read to prevent redundant calls
  const recentlyMarkedAsRead = useRef(new Set());

  // Mark conversation as read
  const markConversationAsRead = useCallback(async (conversationId) => {
    if (!isAuthenticated || !user?.id) return;

    // Prevent redundant calls within a short time window
    const key = `${conversationId}-${user.id}`;
    if (recentlyMarkedAsRead.current.has(key)) {
      return;
    }
    
    recentlyMarkedAsRead.current.add(key);
    
    // Clear the key after 10 seconds to allow future mark-as-read operations
    // This needs to be longer than the polling interval (5s) to prevent override
    setTimeout(() => {
      recentlyMarkedAsRead.current.delete(key);
    }, 10000);

    try {
      // Mark all messages in this conversation as read by updating read_at timestamp
      const { error: messagesError } = await supabase
        .from('chat_messages')
        .update({ read_at: new Date().toISOString() })
        .eq('conversation_id', conversationId)
        .eq('is_dm', true)
        .neq('user_id', user.id) // Only mark messages from other users as read
        .is('read_at', null); // Only update unread messages

      if (messagesError) throw messagesError;

      // Also update dm_read_status for consistency (if the table is used elsewhere)
      const { error: statusError } = await supabase
        .from('dm_read_status')
        .upsert({
          conversation_id: conversationId,
          user_id: user.id,
          last_read: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'conversation_id,user_id'
        });

      if (statusError) {
        console.error('Failed to update dm_read_status:', statusError);
        // Don't throw here as this is secondary - the main update is chat_messages.read_at
      }
      
      // Update local state immediately (optimistic update)
      dispatch({ 
        type: dmActions.UPDATE_READ_STATUS, 
        payload: { conversationId }
      });
      
      // Also update the lastUnreadCountsRef to prevent polling from overriding
      if (lastUnreadCountsRef.current) {
        lastUnreadCountsRef.current.set(conversationId, 0);
      }
      
      // The dm_read_status UPDATE will trigger the global subscription
      // to update unread counts in real-time
    } catch (error) {
      console.error('Failed to mark conversation as read:', error);
    }
  }, [isAuthenticated, user?.id, dispatch]);

  // Set active conversation
  const setActiveConversation = useCallback((conversationId) => {
    dispatch({ type: dmActions.SET_ACTIVE_CONVERSATION, payload: conversationId });
    
    if (conversationId) {
      // Always reload messages to ensure we have the latest data
      loadConversationMessages(conversationId);
      
      // Mark as read
      markConversationAsRead(conversationId);
    }
  }, [dispatch, loadConversationMessages, markConversationAsRead]);

  // Load user suggestions
  const loadUserSuggestions = useCallback(async () => {
    if (!isAuthenticated) return;

    dispatch({
      type: dmActions.SET_USER_SUGGESTIONS,
      payload: {
        teamMembers: [],
        tournamentParticipants: [],
        recentUsers: []
      }
    });
  }, [isAuthenticated, dispatch]);

  // Search users
  const searchUsers = useCallback(async (query) => {
    if (!isAuthenticated || !query.trim() || query.length < 2) {
      dispatch({ type: dmActions.SET_SEARCH_RESULTS, payload: [] });
      return;
    }

    try {
      const { data, error } = await supabase
        .from('users_for_dm')
        .select('*')
        .or(`username.ilike.%${query}%,display_name.ilike.%${query}%`)
        .limit(20);

      if (error) throw error;
      
      dispatch({ type: dmActions.SET_SEARCH_RESULTS, payload: data || [] });
    } catch (error) {
      console.error('Failed to search users:', error);
      setError('Failed to search users');
    }
  }, [isAuthenticated, dispatch, setError]);

  // Optimized unread count polling (single source of truth)
  const startUnreadCountPolling = useCallback(() => {
    if (!isAuthenticated || !user?.id || pollingIntervalRef.current) return;

    const pollUnreadCounts = async () => {
      try {
        const { data, error } = await supabase
          .from('recent_dm_conversations')
          .select('conversation_id, unread_count');

        if (error) {
          console.error('Polling error:', error);
          return;
        }

        // Efficiently detect changes using Map comparison
        const updates = [];
        const currentCounts = new Map();
        
        data?.forEach(conv => {
          const conversationId = conv.conversation_id;
          const currentCount = conv.unread_count || 0;
          const lastCount = lastUnreadCountsRef.current.get(conversationId) || 0;
          const recentlyMarkedKey = `${conversationId}-${user?.id}`;
          const isRecentlyMarked = recentlyMarkedAsRead.current.has(recentlyMarkedKey);
          
          // If we recently marked this conversation as read, don't override with stale data
          if (isRecentlyMarked && currentCount > 0) {
            // Keep the last count as 0 (don't update with stale unread count)
            currentCounts.set(conversationId, 0);
            if (lastCount !== 0) {
              updates.push({
                conversationId,
                unreadCount: 0
              });
            }
          } else {
            currentCounts.set(conversationId, currentCount);
            
            if (currentCount !== lastCount) {
              updates.push({
                conversationId,
                unreadCount: currentCount
              });
            }
          }
        });

        // Update reference in single operation
        lastUnreadCountsRef.current = currentCounts;

        // Single batch update for all changes
        if (updates.length > 0) {
          dispatch({ type: dmActions.BATCH_UPDATE_UNREAD_COUNTS, payload: updates });
        }
      } catch (error) {
        console.error('Error polling unread counts:', error);
      }
    };

    // Initial poll
    pollUnreadCounts();
    
    // Optimized polling interval: 5 seconds (good balance of responsiveness vs performance)
    pollingIntervalRef.current = setInterval(pollUnreadCounts, 5000);
  }, [isAuthenticated, user?.id, dispatch, recentlyMarkedAsRead]);

  const stopUnreadCountPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Setup real-time subscription for a conversation
  const subscribeToConversation = useCallback((conversationId) => {
    if (!conversationId || subscriptionsRef.current[conversationId]) return;

    const channel = supabase.channel(`dm-${conversationId}`, {
      config: {
        broadcast: { self: true },
        presence: { key: user?.id }
      }
    })
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
        filter: `conversation_id=eq.${conversationId}`
      }, async (payload) => {
        try {
          // Fetch user data for the message
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('id, username, avatar_url, country_code')
            .eq('id', payload.new.user_id)
            .single();

          if (userError) {
            console.error('Failed to fetch user data for DM:', userError);
            return;
          }

          const messageWithUser = {
            ...payload.new,
            users: userData
          };

          dispatch({
            type: dmActions.ADD_DM_MESSAGE,
            payload: { conversationId, message: messageWithUser }
          });

          // Update conversation last message time
          dispatch({
            type: dmActions.UPDATE_CONVERSATION,
            payload: {
              conversation_id: conversationId,
              last_message_at: payload.new.created_at,
              last_message_text: payload.new.message
            }
          });

          // If this is the active conversation and message is from another user, mark as read immediately
          if (state.activeConversationId === conversationId && payload.new.user_id !== user?.id) {
            markConversationAsRead(conversationId);
          }
        } catch (error) {
          console.error('Failed to handle new DM message:', error);
        }
      })
      .subscribe();

    subscriptionsRef.current[conversationId] = channel;
  }, [user?.id, dispatch, state.activeConversationId, markConversationAsRead]);

  // Unsubscribe from conversation
  const unsubscribeFromConversation = useCallback((conversationId) => {
    const subscription = subscriptionsRef.current[conversationId];
    if (subscription) {
      subscription.unsubscribe();
      delete subscriptionsRef.current[conversationId];
    }
  }, []);



  // Setup global DM subscription for real-time updates
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    const globalChannel = supabase.channel('global-dm-updates', {
      config: {
        broadcast: { self: false }
      }
    })
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
        filter: `is_dm=eq.true`
      }, async (payload) => {
        try {
          // Only process messages not sent by current user
          if (payload.new.user_id === user.id) return;
          
          const conversationId = payload.new.conversation_id;
          
          // If this conversation is active, the specific subscription will handle it
          // If not, we need to trigger a conversations reload to update unread counts
          if (state.activeConversationId !== conversationId) {
            // Reload conversations to get updated unread counts and last messages
            loadConversations();
          } else {
            // If it's the active conversation, ensure it's marked as read
            setTimeout(() => markConversationAsRead(conversationId), 100);
          }
        } catch (error) {
          console.error('Failed to handle global DM update:', error);
        }
      })
      .subscribe();

    return () => {
      globalChannel.unsubscribe();
    };
  }, [isAuthenticated, user?.id, state.activeConversationId, loadConversations, markConversationAsRead]);

  // Initialize DM system
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    loadConversations();
    loadUserSuggestions();
    startUnreadCountPolling();

    const subscriptions = subscriptionsRef.current;
    const typingTimeouts = typingTimeoutsRef.current;

    return () => {
      // Cleanup all subscriptions
      Object.keys(subscriptions).forEach(conversationId => {
        unsubscribeFromConversation(conversationId);
      });

      // Cleanup polling
      stopUnreadCountPolling();

      // Cleanup typing timeouts
      Object.values(typingTimeouts).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, [isAuthenticated, user?.id, loadConversations, loadUserSuggestions, unsubscribeFromConversation, startUnreadCountPolling, stopUnreadCountPolling]);

  const value = {
    // State
    conversations: state.conversations,
    activeConversationId: state.activeConversationId,
    messagesByConversation: state.messagesByConversation,
    typingUsers: state.typingUsers,
    userSuggestions: state.userSuggestions,
    searchResults: state.searchResults,
    loading: state.loading,
    error: state.error,
    
    // Actions
    createOrGetConversation,
    loadConversationMessages,
    sendDMMessage,
    setActiveConversation,
    markConversationAsRead,
    searchUsers,
    subscribeToConversation,
    unsubscribeFromConversation,
    clearError,
    
    // Utils
    isAuthenticated
  };

  return (
    <DMContext.Provider value={value}>
      {children}
    </DMContext.Provider>
  );
};

export const useDM = () => {
  const context = useContext(DMContext);
  if (!context) {
    throw new Error('useDM must be used within a DMProvider');
  }
  return context;
};